/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React from 'react';
import { isBefore } from 'date-fns';
import { withRouter } from 'react-router';
import {
    object, shape, string, bool, array, func, number,
} from 'prop-types';
import QueryString from 'qs';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import track from 'react-tracking';
import Cookies from 'universal-cookie';

// Icons
import { withApollo } from '@apollo/client/react/hoc';
import mbpLogger from '@module-logger/mbp';

// gql & helper functions
import { GRAPHQL_ENV } from '../../../gql';
import memberDucks from '../../../../state/ducks/Member/ducks';
import * as app from '../../../../state/ducks/App';
import { trackPage, trackProductData } from '../../../helpers/tracking/common/commonTrackingHelpers';
import { saveSortFiltersToLocalStorage, getDomainBrandName } from '../../../helpers/tracking/vendors/Tealium/tealiumProductDataLayerHelpers';
import { getBaseCode, getBrandId } from '../../../helpers/tracking/product/dataLayerProductHelpers';
import getPrice from '../../../helpers/getPrice/getPrice';
import clubQueries from '../../../gql/queries/findClubData';
import productPageQueries from '../../../gql/queries/findProductPageByPartNumber';
import {
    setAgeVerifyFlagInvalid, clearGCIData, setBrandMovieSkus, removeGCIKey, setMiniCartModalState, setDonationsCategory, setSelectedInlineAddons, setSelectedBYOItems,
    setCardIsleData,
} from '../../../../state/ducks/App/App-Actions';
import {
    country, getLocationType, getSSRDeviceType, getGCIData,
} from '../../../../state/ducks/App/App-Selectors';
import brandThemes from '../../AppShell/brandtheme';
import {
    getAddToCartError, getAddToCartLoaderStatus,
} from '../../../../state/ducks/AddToCart/AddToCart-Selectors';
import subscriptionConfig from '../../../helpers/common/subscription/subscription-helpers';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { persistPage } from '../../../helpers/EnterpriseID/CustomerDataPersistance/customerDataPersistance';
import isProductListingAd from '../../../helpers/isProductListingAd';
import { clearAddToCartError, loadAddToCartloader } from '../../../../state/ducks/AddToCart/AddToCart-Actions';
import { getPassportData } from '../../../../state/ducks/Passport/Passport-Selectors';
import { addOgDynamicData, getBreadcrumbArray, dateStringReformat } from './Partials/common/commonProductPageHelper';
import { getOperatingSystem } from '../../../../state/ducks/App/ducks/Config/Config-Helpers';

// Product Page UI components
import GraphqlProductSeoSchema from './Partials/GraphqlProductSeoSchema/GraphqlProductSeoSchema';
import PDPSkeleton from '../GraphqlSkeletonComponents/PDPSkeleton';
import GraphqlClubProductPage from './GraphqlClubProductPage';
import GraphqlPortalLoader from '../GraphqlCommonComponents/GraphqlPortalLoader';
import SiteLoader from '../../../pages/Checkout/containers/Common/SiteLoader/SiteLoader';
import GraphqlCYOProductPageBuilder from './Partials/GraphqlCustomProductPage/GraphqlCYOProductPage/GraphqlCYOProductPageBuilder';
import CovidWarningPopup from './Partials/CovidWarningPopup';
import StandardProductsPage from './Partials/StandardProductsPage/StandardProductsPage';
import { removePmallItem } from '../../../../state/ducks/Common/ducks/PmallPersonalization/PmallPersonalization-Actions';
import MiniCart from '../../Checkout/containers/CheckoutFood/components/Partials/MiniCart/MiniCart';
import ProductNotAvailableContainer from './Partials/ProductNotAvailable/ProductNotAvailableContainer';
import { getActiveABTest } from '../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import triggerPopChatEvent from '../../MultiBrandChatController/AigoChatWidgetWrapperV2/popOpenChat';
import PDPSeoSchemaOutOfStock from './Partials/GraphqlProductSeoSchema/PDPSeoSchemaOutOfStock';
import { trackGtmPageView } from '../../../helpers/tracking/vendors/GTM/gtmHelpers';
import { brandByCode } from '../../Checkout/containers/CheckoutFood/components/Partials/Helper/Helper';

const monthList = app?.ducks?.common?.selectors?.monthList;

const styles = (theme) => ({
    contentTop: {
        // Desktop
        margin: '0px auto',
        maxWidth: '1380px',
        color: theme.palette.black,
        // Tablet
        [theme.breakpoints.down('md')]: {
            margin: '20px 20px 30px',
        },
        // Mobile
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 10px 0',
        },
    },
    contentLeft: {
        width: '100%',
        // Tablet
        [theme.breakpoints.down('md')]: {
            flex: '1 1 auto',
            display: 'flex',
            maxWidth: '40%',
            marginRight: '20px',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down(600)]: {
            maxWidth: '100%',
        },
        marginRight: '24px',
        maxWidth: '665px',
    },
    contentRight: {
        width: '100%',
        maxWidth: '980px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            width: 'calc(60% - 20px)',
            display: 'flex',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    contentBottom: {
        width: '100%',
        margin: '40px auto 60px auto',
        maxWidth: '1380px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 15px 0',
            padding: '0',
        },
    },
    breadCrumbBody: {
        display: 'none',
        margin: '30px auto 10px auto',
        maxWidth: 1380,
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Desktop & Tablet
        '&--desktop': {
            [theme.breakpoints.up('sm')]: {
                display: 'flex',
            },
        },
        // Mobile
        '&--mobile': {
            [theme.breakpoints.down('sm')]: {
                display: 'flex',
                justifyContent: 'center',
            },
        },
        '&--mobile.club-mobile-pdp': {
            [theme.breakpoints.down('sm')]: {
                display: 'block',
                textAlign: 'left',
                padding: '0 10px 0',
                '& li': {
                    display: 'inline-block',
                },
                '& ol': {
                    textAlign: 'left',
                },
            },
        },
    },
    chatBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '23.215px 64.8084px',
        left: '0px',
        right: '0px',
        background: '#F5F5F5',
    },
    chatHeader: {
        // fontFamily: 'Lato',
        fontWeight: '700',
        fontSize: '18px',
        lineHeight: '26px',
        textAlign: 'center',
        letterSpacing: '0.01em',
        color: '#333333',
    },
    chatText: {
        height: '48px',
        // fontFamily: 'Lato',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        textAlign: 'center',
        color: '#333333',
        '@media only screen and (max-width: 600px)': {
            height: '100%',
        },
    },
    chatBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '7.73832px 19.3458px',
        width: '132.18px',
        height: '35.48px',
        background: theme.palette.bgChatMessage,
        border: `0.96729px solid ${theme.palette.bgChatMessage} `,
        borderRadius: '2.90187px',
        flex: 'none',
        order: 0,
        flexGrow: 0,
        '@media only screen and (max-width: 600px)': {
            marginTop: '12px',
        },
    },
    chatLayout: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0px',
        gap: '13.54px',
        width: '93.49px',
        height: '20px',
        flex: 'none',
        order: 0,
        flexGrow: 0,
    },
    chatIcon: {
        flex: 'none',
        order: 0,
        flexGrow: 0,
        top: '1.78px',
        position: 'relative',
    },
    liveChatText: {
        width: 'fit-content',
        height: '20px',
        // fontFamily: 'Lato',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '12px',
        lineHeight: '19px',
        display: 'flex',
        alignItem: 'center',
        textAlign: 'center',
        letterSpacing: '0.025em',
        color: '#FFFFFF',
        flex: 'none',
        order: 1,
        flexGrow: 0,
    },

});

const {
    shopRunner: { shopRunnerActions },
} = memberDucks;

class GraphqlProductPageBuilder extends React.Component {
    constructor(props) {
        super(props);

        // the SKUs can come back in an unpredictable order but they should either be displayed highest price to lowest
        // or lowest to highest which will be set by product in CMS. The following lines sort the SKUs based on that CMS data
        // if sku rankings featureflag (is-pdp-sku-ranking-enabled) is enabled and the rankings are valid then sorting by ranking (ascending) will be used over CMS option
        // .slice is used as to not modify props
        const {
            content, product, brand, productBaseContent, featureFlags, smallestSkuSelectedEnabled,
            removeGCIKeyData, removeGCIData, gciData, trackEventAction,
        } = this.props;
        this.operatingSystem = getOperatingSystem().toLowerCase();
        this.isMobileChatEnabled = ((featureFlags['is-gbm-in-pwa-enabled'] && this.operatingSystem === 'android') || (featureFlags['is-abc-in-pwa-enabled'] && this.operatingSystem === 'ios'));
        const brandId = brand.code;

        const cookies = new Cookies();
        if (cookies.get('banner')) {
            mbpLogger.logInfo({
                appName: process.env.npm_package_name,
                product,
                bannerCookie: cookies.get('banner'),
                message: 'Debug banner cookie [Product page]',
            });
        }

        const productSkus = product ? product.productSkus : null;
        const sku_ranking = productBaseContent?.entries?.[0]?.sku_section?.sku_ranking || content?.entries?.[0]?.sku_section?.sku_ranking;
        if (productSkus) {
            /*
             * @description determines if sku rankings are all valid and should be used for sku sorting. If there are duplicates or a sku is unranked (defaulted to 99) then the skus are not valid. Rank of zero is valid at the moment.
             * @param {array} product skus
             * @return {bool} validity of skus
             */
            const skusHaveDuplicateRanksOrAreUnranked = (skus) => {
                const ranks = new Set();
                return skus.some((sku) => {
                    if (sku.rank === 99) {
                        return true;
                    }
                    return ranks.size === ranks.add(sku.rank).size;
                });
            };

            // Filters out inactive skus based on 'published' and 'displayEndDate' attributes ('Display to Customer' and 'End Date' in PIM)
            const filteredSkus = productSkus.filter((sku) => {
                const skuFormattedEndDate = dateStringReformat(sku.availability?.displayEndDate);
                const skuDateObject = skuFormattedEndDate ? (new Date(skuFormattedEndDate)) : '';
                const skuEndDateIsInPast = isBefore(skuDateObject, new Date());

                const isPublished = sku.availability?.published;

                return isPublished && !skuEndDateIsInPast;
            });
            if (featureFlags['is-pdp-sku-ranking-enabled'] && !skusHaveDuplicateRanksOrAreUnranked(filteredSkus)) {
                this.sortedData = filteredSkus
                    .slice(0)
                    .sort(
                        (a, b) => Number(a.rank) - Number(b.rank),
                    );
            } else if (sku_ranking === 'low to high') {
                this.sortedData = filteredSkus
                    .slice(0)
                    .sort(
                        (a, b) => Number(a.prices?.[0]?.value) - Number(b.prices?.[0]?.value),
                    );
            } else if (sku_ranking === 'high to low') {
                this.sortedData = filteredSkus
                    .slice(0)
                    .sort(
                        (a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value),
                    );
            } else if (sku_ranking === 'next available date') {
                this.sortedData = filteredSkus
                    .slice(0)
                    .sort(
                        (a, b) => new Date(dateStringReformat(a.availability.earliestShipDate)).getTime() - new Date(dateStringReformat(b.availability.earliestShipDate)).getTime(),
                    );
            } else {
                this.sortedData = filteredSkus
                    .slice(0)
                    .sort(
                        (a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value),
                    );
            }
            if (brandThemes[brandId]) {
                mbpLogger.logDebug({
                    appName: process.env.npm_package_name,
                    message: `GraphqlProductPageBuilder: brandThemes is set for brand "${brandId}"`,
                    brandTheme: brandThemes[brandId],
                });
            } else {
                mbpLogger.logDebug({
                    appName: process.env.npm_package_name,
                    message: `GraphqlProductPageBuilder: brandThemes is NOT set for brand "${brandId}"`,
                });
            }
            const setImplementProductSubscription = (brandThemes[brandId]?.implement_product_subscription || false);

            // to prioritize default sku as initial selected sku
            const defaultSkuIndex = this.sortedData.findIndex((sku) => sku?.isDefaultSku);
            let defaultSku = this.sortedData[0] || {};

            if (smallestSkuSelectedEnabled) {
                defaultSku = this.sortedData[this.sortedData?.length - 1] || {};
            }

            if (defaultSkuIndex > -1 && !smallestSkuSelectedEnabled) {
                defaultSku = this.sortedData[defaultSkuIndex];
            }

            this.state = {
                validVaseConfiguratorSku: true,
                selectedSku: JSON.parse(JSON.stringify(defaultSku)),
                hasAddons: (product?.addonGroups?.length > 0),
                subscription: {
                    subscriptionKey: subscriptionConfig.subscription_keys,
                    subscriptionType: subscriptionConfig.subscription_type,
                    implementProductSubscription: setImplementProductSubscription,
                    viewSubscription: false,
                    isTypeDiscovery: false,
                    subscriptionCheckbox: false,
                },
                subscriptionDetails: {
                    subscriptionType: '',
                    subscriptionDuration: '',
                    subscriptionFrequency: '',
                    subscriptionDurationKey: '',
                },
                // club page handler
                isClubProductLoaded: false,
                clubSkuMonthGroup: {},
                clubComponents: [],
                clubUpsells: [],
                hasWine: (defaultSku?.ageVerifyFlag === 'true'),
                isAddToCartEnabled: !(defaultSku?.ageVerifyFlag === 'true'),
                loader: false,
                selectedNonUpsellSku: defaultSku,
                disableAddToCart: false,
                personalizedData: {},
                minicartModalOpen: false,
                quantity: '1',
                baseProduct: null,
                mobileAgeVerificationOpen: false,
                addToCartData: null,
                covidWarningOpen: false,
                hide18FCheckbox: false,
                selectedAddons: {
                    cart: [],
                    list: [],
                },
                isStandAloneMovieSku: product?.parentProduct?.categories?.includes('digital_products') || product?.name?.toLowerCase()?.includes('digital movie gift'),
            };

            if (!(featureFlags['is-club-pdp-from-pim'] && product?.productType?.toLowerCase() === 'club')) {
                const productUpsell = product ? product.upsells : [];
                this.sortedData = [...this.sortedData, ...productUpsell];
            }

            this.getProductPageComponents();
        }
        if (featureFlags['is-pdp-subscription-enabled']) {
            /**
             * Cloned object so we can update the value of this array
             */
            this.sortedData = JSON.parse(JSON.stringify(this.sortedData));
        }

        if (gciData?.productId !== this.sortedData?.[0]?.id) {
            removeGCIKeyData();
            removeGCIData({});
        }

        /**
         * Queued events before render so, it will be fire after page view.
         */
        if (featureFlags['is-pdp-v-2-enabled']) {
            trackEventAction({
                eventCategory: 'Test Impression',
                eventAction: 'PDP Design - Variant',
                queued: true,
            });
        }
    }

    componentDidMount() {
        const {
            loadSr,
            featureFlags,
            product,
            resetAgeVerificationFlag,
            actionSelectedInlineAddons,
            isPla,
            urlParams,
            callClearAddToCartError,
            location,
            locationType,
            actionSetBrandMovieSkus,
            setLoader,
            actionSetDonationCategory,
            deviceType,
            presentation_family,
        } = this.props;

        if (typeof window !== 'undefined') {
            window.productCategories = product?.categories;
        }
        if (typeof document !== 'undefined' && deviceType !== 'mobile' && document.getElementById('root')?.style && document.getElementById('root')?.style?.position !== 'static' && presentation_family === 'flower') {
            document.getElementById('root').style.position = 'static';
        }
        const { isClubProductLoaded, selectedSku, isStandAloneMovieSku } = this.state;
        if (locationType === 'Hospital') {
            this.setState({
                covidWarningOpen: true,
            });
        }
        actionSetDonationCategory(product?.categories?.[0] || '');
        const params = QueryString.parse(location.search.replace(/^\?/, ''));
        if (product?.productType?.toLowerCase() === 'club') {
            const { club, partNumber } = product;
            this.setClubProduct(partNumber, isClubProductLoaded, club?.defaultSku?.month, club?.allItemsList, club?.defaultSku?.upsells, club?.defaultSku?.components, null, true);
            if (params?.ed || params?.selectSku) {
                this.getClubSku(params?.ed || params.selectSku, true);
            }
        }
        if (params?.ed || params?.selectSku) {
            let skuByParam = this.sortedData.find((sku) => sku.id?.split('-')?.pop() === (params?.ed || params?.selectSku));
            if (!skuByParam) {
                skuByParam = this.sortedData.find((sku) => sku.id === (params?.ed || params?.selectSku));
            }
            if (skuByParam) {
                this.setState({
                    selectedSku: skuByParam,
                    hasWine: (skuByParam.ageVerifyFlag === 'true'),
                    isAddToCartEnabled: !(skuByParam.ageVerifyFlag === 'true'),
                    selectedNonUpsellSku: skuByParam,
                });
            }
        }
        if (location?.state?.selectedId) {
            const skuByParams = this.sortedData.find((sku) => sku.id === location?.state?.selectedId);
            if (skuByParams) {
                this.setState({
                    selectedSku: skuByParams,
                });
            }
        }
        const isShopRunnerEnabled = featureFlags['is-shoprunner-enabled'];
        if (isShopRunnerEnabled) {
            loadSr(); // calls shoprunner scripts to load
        }

        this.productSubscription();
        resetAgeVerificationFlag(false);

        if (urlParams) {
            /**
             * PLA Channel
             * - Partnumber from PLA is contained in either `sku` or `adid` param
             * - Order of url params are not constant. Below accounts for param as first value or n+1
             */
            if (isPla || location.search.includes('sku')) {
                const partNumber = urlParams?.['?adid'] || urlParams?.adid || urlParams?.['?sku'] || urlParams?.sku;
                this.setDefaultSku(partNumber);
            }
            /**
             * PDP Default Sku Select
             * - Example, sku is only url param (e.g. ?123456M)
             */
            if (Object.keys(urlParams).length === 1 && !selectedSku) {
                const selectedPartNumber = location.search.split('?')[1];
                if (/\d/.test(selectedPartNumber)) {
                    this.setDefaultSku(selectedPartNumber);
                }
            }
        }
        callClearAddToCartError();
        actionSelectedInlineAddons([]);
        if (isStandAloneMovieSku) {
            const movieSkus = product?.productSkus?.reduce((acc, sku) => {
                const { id } = sku;
                // Starting at the end of the string (backwards pattern matching), continue to match any characters that aren't '-' or '_'
                return { ...acc, [id.match(/[^-_]*$/)]: true };
            }, {});
            actionSetBrandMovieSkus(movieSkus);
        }

        if (typeof window !== 'undefined') {
            window.real_product_category = product.categories;
        }

        setLoader(false); // need this to prevent infinite loading on pdp page
    }

    trackViewItemForSkuChange = (selectedSku = {}) => {
        const {
            brand,
            location,
            product,
        } = this.props;
        const skuId = selectedSku?.id;
        const selectedSkuPrices = selectedSku?.dynamicPrice?.prices || selectedSku?.prices || [];
        const price = getPrice(selectedSkuPrices)?.value || 0;
        const itemFulfillmentMethod = selectedSku?.availability?.productDeliveryType || product?.availability?.productDeliveryType || '';
        const rawItemType = this.state?.subscription?.subscriptionCheckbox ? 'subscription' : (product?.productType || 'standard');
        const itemType = rawItemType === 'REGULARPRODUCT' ? 'REGULAR_PRODUCT' : rawItemType;
        const itemListId = location?.state?.categoryId || product?.categoryId;
        const itemListName = location?.state?.categoryName || product?.categoryName;
        const itemListPath = location?.state?.categoryPath || product?.categoryPath || '';
        const itemBrand = brand?.domain || product?.brand;
        const itemListPosition = location?.state?.position || undefined;
        const currency = selectedSku?.currency || product?.currency || 'USD';

        if (!skuId || !product) {
            return;
        }

        trackGtmPageView({
            ga_event_name: 'product_page_view',
            eventCategory: 'Product Page',
            eventAction: 'View Item',
            eventLabel: skuId,
            product_ids: [getBaseCode(product?.partNumber || product?.legacyId || skuId)],
            product_names: [product?.name || ''],
            product_quantities: [1],
            product_variant_type: ['selected'],
            product_brand_name_expanded: [brandByCode[getBrandId(product?.partNumber)]],
            product_list_names: [itemListName],
            product_list_currency: [currency],
            product_real_category_name: [itemListName],
            // product_real_category_name_2: product?.categories?.length ? [product?.categories?.[1]] : [''],
            // product_real_category_name_3: product?.categories?.length ? [product?.categories?.[2]] : [''],
            // product_real_category_name_4: product?.categories?.length ? [product?.categories?.[3]] : [''],
            // product_real_category_name_5: product?.categories?.length ? [product?.categories?.[4]] : [''],

            order_brandName_list: [brandByCode[getBrandId(product?.partNumber)]],

            product_category_ids: [itemListId],
            google_business_vertical: ['retail'],
            product_item_type_state: [itemType === 'REGULARPRODUCT' ? 'REGULAR_PRODUCT' : itemType],
            product_skus: [skuId],
            products_currency: 'USD',
            product_delivery_types: [itemFulfillmentMethod],
            product_price: price,
            product_prices: [price],
            product_affiliation: [getDomainBrandName(typeof window !== 'undefined' ? window?.location?.host : '')],
            index: itemListPosition ? [itemListPosition] : undefined,
            products: [{
                ...product,
                selectedSku: skuId,
            }],
            category_id: itemListId || '',
            category_name: itemListName || '',
            category_path: itemListPath,
        }, '', 'product_page_view');
    };

    componentDidUpdate(prevProps, prevState) {
        const {
            brand, loadSr, featureFlags, locationType,
        } = this.props;
        const isShopRunnerEnabled = featureFlags['is-shoprunner-enabled'];
        // Fall back to call only if brandId is set
        if (prevProps.brand.code !== brand.code && isShopRunnerEnabled) {
            loadSr();
        }

        if (locationType === 'Hospital' && prevProps.locationType !== 'Hospital') {
            this.setState({
                covidWarningOpen: true,
            });
        }
    }

    componentWillUnmount() {
        const {
            featureFlags, callRemovePmallItem, product, actionSetMiniCartModalState, selectedBYOItems, removeCardIsleData,
        } = this.props;

        if (typeof window !== 'undefined') {
            window.productCategories = undefined;
        }
        const { selectSku } = this.state;
        const isPmallPersonalizationAttr = (selectSku?.attributes || [])?.find((attr) => attr.name === 'is_pmall_personalization_enabled');
        const isPmallPersonalizationEnabled = isPmallPersonalizationAttr?.values?.[0]?.value === 'true';
        const isPmallPersonalizationTypeAttr = (selectSku?.attributes || [])?.find((attr) => attr.name === 'personalization_type_enabled');
        const pmallPersonalizationType = !!isPmallPersonalizationTypeAttr?.values?.[0]?.value;

        selectedBYOItems({ });
        removeCardIsleData({ });
        if (featureFlags['is-pmall-enabled'] && (product?.brandId === '1030' || product?.brandId === '1033' || isPmallPersonalizationEnabled || pmallPersonalizationType)) {
            callRemovePmallItem();
        }
        actionSetMiniCartModalState(false);
    }

    setVaseConfiguratorSku = (sku) => {
        if (!sku) {
            this.setState({
                validVaseConfiguratorSku: false,
            });
            return;
        }

        this.setState({
            selectedSku: sku,
            validVaseConfiguratorSku: true,
        });
    }

    setClubProduct = (productID, isClubProductLoaded, selectedMonth, allItems = [], upSells = [], components = [], preSelectedSku = null, initialLoad = false) => {
        let skuData = this.sortedData;
        if (!isClubProductLoaded) {
            const availableMonth = selectedMonth || monthList[new Date().getMonth()];
            const clubSku = [];
            const skuMonthGroup = {};
            skuData = skuData.slice(0)
                .sort(
                    (a, b) => new Date(dateStringReformat(a?.availability?.earliestShipDate)).getTime() - new Date(dateStringReformat(b?.availability?.earliestShipDate)).getTime(),
                );

            skuData.forEach((sku) => {
                const skuMapper = { ...sku };  // cloned to handle immutability
                const skuMonth = monthList[new Date(dateStringReformat(sku.availability?.earliestShipDate)).getMonth()];
                const dateObject = {
                    month: skuMonth,
                    year: new Date(dateStringReformat(sku.availability?.earliestShipDate))?.getFullYear(),
                    day: new Date(dateStringReformat(sku.availability?.earliestShipDate))?.getDate(),
                };
                skuMapper.partNumber = sku.partNumber || sku.id || null;
                skuMapper.dateInt = `${dateObject.year}-${dateObject.month}-${dateObject.day}`;
                skuMapper.date = dateObject;
                skuMapper.fade = false;
                skuMapper.fullDate = new Date(dateStringReformat(sku.availability?.earliestShipDate));
                skuMapper.isSelectedMonth = (availableMonth === monthList[new Date(dateStringReformat(skuMapper?.availability?.earliestShipDate)).getMonth()]);
                if (typeof skuMonthGroup[skuMonth] !== 'undefined') {
                    skuMonthGroup[skuMonth].push(skuMapper);
                } else if (skuMonth) {
                    skuMonthGroup[skuMonth] = [];
                    skuMonthGroup[skuMonth].push(skuMapper);
                }

                if (initialLoad) {
                    const clubMapper = allItems?.filter((item) => item?.id === sku?.legacyId) || [];
                    skuMapper.clubLength = clubMapper?.[0]?.frequency || 0;
                    skuMapper.tagline = clubMapper?.[0]?.frequency === '12' ? 'Best value' : null;
                    const startDate = new Date(dateStringReformat(sku.availability?.earliestShipDate));
                    const componentEndDate = startDate.setMonth(startDate.getMonth() + (Number(clubMapper?.[0]?.frequency) - 1));
                    if (componentEndDate) {
                        skuMapper.endDate = {
                            month: monthList[new Date(componentEndDate)?.getMonth()],
                            year: new Date(componentEndDate)?.getFullYear(),
                        };
                    }
                }

                clubSku.push(skuMapper);
            });

            Object.keys(skuMonthGroup).forEach((month) => {
                skuMonthGroup[month] = skuMonthGroup[month].slice(0)
                    .sort(
                        (a, b) => Number(b.prices[0].value) - Number(a.prices[0].value),
                    );
            });
            skuData = clubSku;
            if (skuData) {
                skuData = skuData
                    .slice(0)
                    .sort(
                        (a, b) => Number(b.prices[0].value) - Number(a.prices[0].value),
                    );

                let selectedSkuFromList = skuData?.filter((sku) => (sku.partNumber === productID));

                if (selectedSkuFromList.length === 0) {
                    selectedSkuFromList = skuData?.filter((sku) => sku.date.month === availableMonth);
                }

                const { clubUpsells, clubSkuMonthGroup, selectedNonUpsellSku } = this.state;
                let upsellsCollections = upSells || [];
                let clubSkuMonthCollections = skuMonthGroup;
                if (components.length > 0) {
                    Object.keys(skuMonthGroup).forEach((month) => {
                        const hasComponent = components.filter((component) => component?.month === skuMonthGroup[month]?.[0]?.date?.month);
                        if (hasComponent.length === 0 && skuMonthGroup[month].length > 0) {
                            clubSkuMonthCollections[month][0].fade = true;
                        } else {
                            clubSkuMonthCollections[month][0].fade = false;
                        }
                    });
                }

                let skuSelection = selectedSkuFromList.length > 0 ? selectedSkuFromList[0] : skuData[0];
                if (preSelectedSku) {
                    this.sortedData.push(preSelectedSku);
                    skuSelection = preSelectedSku;
                    upsellsCollections = clubUpsells;
                    clubSkuMonthCollections = clubSkuMonthGroup;
                    // sorted by price after adding upsell
                    this.sortedData = this.sortedData.slice(0)
                        .sort(
                            (a, b) => Number(b?.prices?.[0]?.value) - Number(a?.prices?.[0]?.value),
                        );
                } else {
                    upsellsCollections = this.calculateUpsellPrice(upsellsCollections, skuSelection);
                    // updating sku collection after adding required variables for clubs
                    this.sortedData = skuData;
                }
                this.setState({
                    clubComponents: components || [],
                    clubUpsells: upsellsCollections,
                    isClubProductLoaded: true,
                    selectedSku: skuSelection,
                    selectedNonUpsellSku: preSelectedSku ? selectedNonUpsellSku : skuSelection,
                    clubSkuMonthGroup: clubSkuMonthCollections,
                    selectedClubUpsells: preSelectedSku?.id || null,
                });
            }
        }
    }

    setProductQuantity = (qty) => {
        const quantity = qty || '1';
        this.setState({
            quantity,
        });
    }

    handleMobileAgeVerification = (flag, addToCartData = null) => {
        this.setState({
            mobileAgeVerificationOpen: flag,
            addToCartData,
        });
    }

    closeCovidWarning = () => {
        this.setState({
            covidWarningOpen: false,
        });
    }

    calculateUpsellPrice = (upsells, sku) => {
        const upsellsCollections = upsells.slice();
        upsellsCollections.forEach((item, index) => {
            upsellsCollections[index] = { ...upsellsCollections[index] };
            upsellsCollections[index].upSellCalculatedPrice = parseFloat((Number(item.upSellDisplayPrice) - sku?.prices?.[0].value).toFixed(2));
        });
        return upsellsCollections;
    }

    changeClubLength = async (productId, skuSelected) => {
        const { clubSkuMonthGroup, selectedSku: selectSku } = this.state;
        if (selectSku.id === skuSelected) return;
        const hasUpsells = this.sortedData.findIndex((sku) => (sku?.isUpsellSku));
        if (hasUpsells >= 0) {
            this.sortedData.splice(hasUpsells, 1);
            this.sortedData = this.sortedData.slice(0)
                .sort(
                    (a, b) => Number(b.prices[0].value) - Number(a.prices[0].value),
                );
        }
        const data = await this.getClubComponents(productId, true);
        if (data?.item) {
            const setFetchedClubMonths = clubSkuMonthGroup;
            const fetchedComponents = data?.item?.components || [];
            Object.keys(clubSkuMonthGroup).forEach((month) => {
                const hasComponent = fetchedComponents.filter((component) => component?.month === clubSkuMonthGroup[month]?.[0]?.date?.month);
                if (hasComponent.length === 0 && clubSkuMonthGroup[month].length > 0) {
                    setFetchedClubMonths[month][0].fade = true;
                } else {
                    setFetchedClubMonths[month][0].fade = false;
                }
            });
            const selectedSku = this.sortedData.find((sku) => sku.id === skuSelected);
            let upsells = data?.item?.upsells;
            if (upsells) {
                upsells = this.calculateUpsellPrice(upsells, selectedSku);
            }

            this.setState({
                selectedSku,
                clubSkuMonthGroup: setFetchedClubMonths,
                clubComponents: fetchedComponents,
                clubUpsells: upsells || [],
                selectedClubUpsells: null,
                selectedNonUpsellSku: this.sortedData.find((sku) => sku.id === skuSelected),
            });
        }
    }

    changeClubMonth = (sku) => {
        if (sku?.partNumber || sku?.id) {
            this.getClubSku(sku?.partNumber || sku?.id, true);
        }
    }

    changeClubUpsell = async (productId, deleteOnly = false) => {
        const { featureFlags } = this.props;
        if (productId) {
            const upsellToSku = {};
            upsellToSku.isUpsellSku = true;
            const { selectedNonUpsellSku, clubUpsells } = this.state;
            if (!deleteOnly) {
                const selectedUpsell = clubUpsells?.find((upsell) => upsell?.id === productId || upsell?.partNumber === productId) || {
                    id: productId,
                    isUpsellSku: true,
                };
                this.trackVariantSelection({ ...selectedUpsell, id: selectedUpsell?.id || productId, isUpsellSku: true });
            }
            const hasAlready = this.sortedData.filter((sku) => sku.id === productId);
            const hasUpsells = this.sortedData.findIndex((sku) => (sku?.isUpsellSku));
            if (deleteOnly && hasUpsells >= 0) {
                this.sortedData.splice(hasUpsells, 1);
                this.sortedData = this.sortedData.slice(0)
                    .sort(
                        (a, b) => Number(b.prices[0].value) - Number(a.prices[0].value),
                    );
                this.setState({
                    selectedSku: selectedNonUpsellSku,
                    selectedClubUpsells: null,
                });
                return;
            }
            if (hasAlready.length) {
                this.setState({
                    selectedSku: hasAlready[0],
                });
                return;
            }
            let data = await this.getClubComponents(productId, false, false);
            const { selectedSku } = this.state;
            if (featureFlags['is-club-pdp-from-pim']) {
                data = await this.getClubComponents(productId, false, false, true);
            }
            Object.keys(selectedSku).forEach((skuKey) => {
                if (typeof data[skuKey] === 'undefined') {
                    upsellToSku[skuKey] = selectedSku[skuKey];
                } else {
                    upsellToSku[skuKey] = data[skuKey];
                }
            });
            this.getClubSku(productId, true, upsellToSku);
        }
    }

    getClubSku = (productID, items = false, preSelectedSku = null) => {
        const hasUpsells = this.sortedData.findIndex((sku) => (sku?.isUpsellSku));
        if (hasUpsells >= 0) {
            this.sortedData.splice(hasUpsells, 1);
            this.sortedData = this.sortedData.slice(0)
                .sort(
                    (a, b) => Number(b.prices[0].value) - Number(a.prices[0].value),
                );
        }
        this.getClubComponents(productID, items).then(
            (data) => {
                this.setClubProduct(
                    productID,
                    false,
                    data?.item?.description?.month || monthList[new Date().getMonth()],
                    data?.allItemSummary,
                    data?.item?.upsells || [],
                    data?.item?.components || [],
                    preSelectedSku,
                    false,
                    productID,
                );
            },
        );
    }

    getClubDataFromObject = (product, type = 'product') => {
        if (type === 'product') {
            const modifyData = {
                item: {
                    ...product?.defaultSku,
                    description: {
                        startDate: product?.defaultSku?.startDate,
                        availableDate: product?.defaultSku?.availableDate,
                        month: product?.defaultSku?.month,
                        fullImage: product?.defaultSku?.image?.name,
                    },
                },
                brandId: '',
                priceRange: '',
                allItemSummary: {},
                description: {},
            };
            modifyData.allItemSummary = product?.allItemsList;
            return modifyData;
        }
        const modifyData = {
            item: {
                ...product,
                description: {
                    startDate: product?.startDate,
                    availableDate: product?.availableDate,
                    month: product?.month,
                    fullImage: product?.image?.name,
                    name: product?.name,
                },
            },
        };
        return modifyData;
    }

    getProductByPartNumber = (query, context, variables = {}) => {
        const { client } = this.props;
        return client.query({ query, context, variables }).then((res) => res);
    }

    getProductPageComponents = () => {
        const { brand, baseProductPartNumber, experimentParams } = this.props;
        const { baseProduct } = this.state;
        if (baseProductPartNumber) {
            const {
                targeting,
                context,
                isGraphqlTargetingEnabled,
                dynamicPriceInGql,
            } = experimentParams;
            const PRODUCT_BY_PART = productPageQueries(brand?.domain, baseProductPartNumber, null, dynamicPriceInGql, targeting);
            this.getProductByPartNumber(
                PRODUCT_BY_PART,
                context,
                isGraphqlTargetingEnabled ? { targeting } : {},
            ).then((res) => {
                const productSku = res?.data?.findProductByPartNumber?.productSkus || [];
                const upsellSku = productSku.find((sku) => getBaseCode(baseProductPartNumber) === getBaseCode(sku?.id)) || null;
                if (upsellSku) {
                    upsellSku.id = res?.data?.findProductByPartNumber?.partNumber || null;
                    // eslint-disable-next-line no-underscore-dangle
                    upsellSku.__typename = 'Upsell';
                    this.sortedData.push(upsellSku);
                    if (baseProduct?.[0]?.id !== upsellSku?.id && baseProductPartNumber) {
                        this.setState({
                            baseProduct: [upsellSku],
                        });
                    }
                }
            }).catch((error) => {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    query: PRODUCT_BY_PART,
                    component: 'GraphqlProductPageBuilder.js',
                    message: 'Error loading data from Graphql',
                    env: GRAPHQL_ENV,
                    error,
                });
            });
        }
    }

    getClubQuery = (query) => {
        const { client } = this.props;
        return client.query({ query }).then((res) => res);
    }

    getClubComponents = async (productId, items = false, isComponent = true, isAddons = false) => {
        this.setState({ loader: true });
        const { brand, featureFlags } = this.props;
        const { findClubData, findSkuById } = clubQueries;

        const PRODUCT_CLUBS = findClubData(brand?.domain, productId);

        const clubDataByService = new Promise(((resolve) => {
            if (items || (featureFlags['is-club-pdp-from-pim'] && isAddons)) {
                if (featureFlags['is-club-pdp-from-pim']) {
                    const PRODUCT_CLUBS_ITEMS = findSkuById(brand?.domain, productId);
                    this.getClubQuery(PRODUCT_CLUBS_ITEMS).then(({ data }) => {
                        this.setState({ loader: false });
                        if (!items) {
                            resolve(data?.findSkuById);
                        } else {
                            resolve(this.getClubDataFromObject(data?.findSkuById?.clubItem, 'items'));
                        }
                    });
                } else {
                    this.getClubQuery(PRODUCT_CLUBS).then((res) => {
                        const legacyId = res?.data?.findProductByPartNumber?.legacyId;
                        const PRODUCT_CLUBS_ITEMS = findSkuById(brand?.domain, legacyId);
                        this.getClubQuery(PRODUCT_CLUBS_ITEMS).then(({ data }) => {
                            this.setState({ loader: false });
                            resolve(this.getClubDataFromObject(data?.findSkuById?.clubItem, 'items'));
                        });
                    });
                }
            } else {
                this.getClubQuery(PRODUCT_CLUBS).then((res) => {
                    const data = res?.data?.findProductByPartNumber?.club;
                    this.setState({ loader: false });
                    if (isComponent) {
                        resolve(this.getClubDataFromObject(data));
                    }
                    resolve(res?.data?.findProductByPartNumber);
                });
            }
        }));

        const clubReturnData = await clubDataByService;
        return clubReturnData;
    }

    handleMiniCartModalClick = (setModal) => {
        const { minicartModalOpen } = this.state;
        if (minicartModalOpen !== setModal) {
            this.setState({ minicartModalOpen: setModal });
        }
    };

    /**
     * @description Updates the seleced variant
     * @param {string} productId — variant id
     */
    handleSkuClick = (productId) => {
        let submitSlectedSku = this.sortedData.find((sku) => sku.id === productId);
        submitSlectedSku = { ...submitSlectedSku, priceRules: null };
        this.trackVariantSelection(submitSlectedSku);
        this.setState({
            selectedSku: submitSlectedSku,
        });
    };

    trackVariantSelection = (selectedSku) => {
        const skuId = selectedSku?.id || '';
        const isUpsell = selectedSku?.__typename === 'Upsell' || selectedSku?.isUpsellSku;
        this.props.dispatchEmitCustomTrackingEvent({
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: isUpsell ? 'PDP Upsell' : 'Variant Selection',
            label: isUpsell ? 'Select Upsell' : 'Variant Selected',
            tag: skuId,
            page: { type: 'product' },
            products: [{
                baseCode: this.props?.product?.partNumber || selectedSku?.id || '',
                partNumber: skuId,
                price: isUpsell ? Number(selectedSku?.upSellDisplayPrice) : getPrice(selectedSku.prices)?.value || 0,
                quantity: selectedSku?.quantity || 1,
                name: this.props?.product?.name,
            }],
        });
    };

    handleSkuChange = (e, dynamicPriceRules, dynamicPriceData) => {
        const { featureFlags, callRemovePmallItem } = this.props;
        if ((!e || !e.target?.value) && dynamicPriceData !== null) {
            this.setState((prevState) => ({
                selectedSku: {
                    ...prevState.selectedSku,
                    dynamicPrice: dynamicPriceData,
                },
            }));
            return;
        }
        let submitSlectedSku = this.sortedData.find((sku) => sku.id === e.target.value);
        submitSlectedSku = { ...submitSlectedSku, priceRules: dynamicPriceRules, dynamicPrice: dynamicPriceData };
        this.trackVariantSelection(submitSlectedSku);
        this.setState({
            selectedSku: submitSlectedSku,
            hasWine: (submitSlectedSku.ageVerifyFlag === 'true'),
            isAddToCartEnabled: !(submitSlectedSku.ageVerifyFlag === 'true'),
        });
        if (featureFlags['is-pmall-enabled']) {
            callRemovePmallItem();
        }

        if (typeof window !== 'undefined') {
            const productPartNumber = this.props?.product?.partNumber || submitSlectedSku?.id || '';
            saveSortFiltersToLocalStorage({
                partNumber: productPartNumber,
                variant: submitSlectedSku?.id,
                variant_type: 'selected',
            });
            if ((window.foodConfiguratorLayout && window.foodConfiguratorMounted) || !window.foodConfiguratorLayout) {
                this.trackViewItemForSkuChange(submitSlectedSku);
            }
        }
    };

    handleRemoveAddon = (item) => {
        const { selectedAddons: { cart, list } } = this.state;
        this.setState({
            selectedAddons: {
                cart: cart.filter((addon) => addon.productCode !== item.id),
                list: list.filter((addon) => addon.id !== item.id),
            },
        });
    };

    handleAddonChange = (selectedAddons, selectionList) => {
        this.setState({ selectedAddons: { cart: selectedAddons, list: selectionList } });
    }

    /**
     * @description This will extract the subscription data and maintain the state of intial selection in case of Discovery type
     * @memberof GraphqlProductPageBuilder
     */
    productSubscription = () => {
        const {
            subscription,
            subscription: {
                // subscriptionKey,
                // subscriptionType,
                implementProductSubscription,
            },
            selectedSku,
        } = this.state;
        const {
            featureFlags,
            // product,
        } = this.props;
        const setSubscriptionDetails = {};
        /**
         * If subscription attribute doesn't have value
         */
        if (
            !setSubscriptionDetails.subscriptionType && featureFlags['is-pdp-subscription-enabled']) {
            setSubscriptionDetails.subscriptionType = selectedSku.subscriptionsList?.type?.[0] ? selectedSku.subscriptionsList?.type?.[0]?.toString() : ''; // default value
            setSubscriptionDetails.subscriptionFrequency = selectedSku.subscriptionsList?.interval?.[0] ? selectedSku.subscriptionsList?.interval?.[0]?.toString() : ''; // default value
            setSubscriptionDetails.subscriptionDuration = selectedSku.subscriptionsList?.duration?.[0] ? selectedSku.subscriptionsList?.duration?.[0]?.toString() : ''; // default value
            setSubscriptionDetails.subscriptionDurationKey = selectedSku.subscriptionsList?.duration?.[0] ? selectedSku.subscriptionsList?.duration?.[0]?.toString() : ''; // default value
        }
        if (setSubscriptionDetails && setSubscriptionDetails.subscriptionType && setSubscriptionDetails.subscriptionType !== '') {
            const subscriptionTypeInfo = setSubscriptionDetails.subscriptionType.toLowerCase?.();
            let isTypeDiscovery = false;
            let viewSubscription = false;
            // this will check if the type is discovery
            const discoveryViews = ['discovery', 'monthonly', 'monthonlypayfull'];
            if (subscriptionTypeInfo) {
                isTypeDiscovery = discoveryViews.includes(subscriptionTypeInfo?.toLowerCase?.());
                viewSubscription = (implementProductSubscription && subscriptionTypeInfo !== '');
            }
            // if the type is discovery by default it proceed as a default selected subscription
            const subscriptionCheckbox = (isTypeDiscovery);
            this.setState({
                subscriptionDetails: setSubscriptionDetails,
                subscription: {
                    ...subscription, isTypeDiscovery, viewSubscription, subscriptionCheckbox,
                },
            });
        }
    }

    triggerSubscriptionCheck = (event, newValue, forceDropPriceRule = false) => {
        const { subscription, selectedSku } = this.state;
        this.setState((prevState) => ({
            subscription: { ...subscription, subscriptionCheckbox: newValue },
            hide18FCheckbox: !prevState.hide18FCheckbox,
        }));
        const {
            trackEventAction, location, featureFlags, history, subscriptionBannerType,
        } = this.props;

        // 'variant', new subscription pdp banner
        if (subscriptionBannerType === 'variant') {
            trackEventAction({
                eventCategory: 'Subscriptions PDP Modal',
                eventAction: 'click',
                eventLabel: newValue ? 'Checked' : 'Unchecked',
            });
        } else {
            trackEventAction({
                ga_event_name: 'subscribe',
                eventCategory: 'Product Subscription',
                eventAction: 'Subscribe to item',
                eventLabel: newValue ? 'Selected' : 'Deselected',
            });
        }

        if (featureFlags['is-pdp-subscription-enabled']) {
            const searchObject = QueryString.parse(location?.search?.replace(/^\?/, '') || ''); // removing "?" from URL
            if (newValue && !searchObject.subscription) {
                const subscriptionUrl = (location?.search === '') ? `${location?.pathname}?subscription=Basic` : `${location?.pathname}${location?.search}&subscription=Basic`;
                const histOb = {
                    pathname: location?.pathname || subscriptionUrl,
                    search: (location?.search === '') ? '?subscription=Basic' : `${location?.search}&subscription=Basic`,
                    state: location?.state || '',
                };
                history.replace(histOb);
            } else {
                // This is only call if selected sku doesn't have any subscription but URL has subscription param
                if (forceDropPriceRule && searchObject?.subscription) {
                    // eslint-disable-next-line no-undef
                    selectedSku.priceRules = [];
                }
                delete searchObject.subscription; // removing subscription param
                const searchParams = QueryString.stringify(searchObject);

                const histOb = {
                    pathname: `${location?.pathname}`,
                    search: searchParams ? `?${searchParams}` : '',
                    state: location.state || '',
                    hash: location?.hash || '',
                };
                history.replace(histOb);
            }
        }
    }

    handlePersonalizeInputChange = (valueCount) => {
        if (valueCount === 0) {
            this.setState({ disableAddToCart: true });
        } else {
            this.setState({ disableAddToCart: false });
        }
    }

    /**
     * @description Render the mini cart
     */
    getMiniCart = () => {
        const {
            showLoadingMessage, product, presentation_family, isPassportBundleProduct, actionSetMiniCartModalState,
        } = this.props;
        const { minicartModalOpen, selectedSku } = this.state;
        return (
            <>
                {(showLoadingMessage && minicartModalOpen)
                    && (
                        <SiteLoader />
                    )}
                <MiniCart
                    handleMiniCartModalClick={this.handleMiniCartModalClick}
                    minicartModalOpen={minicartModalOpen}
                    actionSetMiniCartModalState={actionSetMiniCartModalState}
                    product={selectedSku || product}
                    presentation_family={presentation_family}
                    isPassportBundleProduct={isPassportBundleProduct || {}}
                    skuData={this.sortedData}
                />
            </>
        );
    }

    getUpdatedPersonalizedProdData = (personalizedData) => {
        if (personalizedData) {
            this.setState({ personalizedData });
        }
    }

    /**
     * @description Checks url for partnumber and sets default sku
     * @param {object} object — url parameters
     */
    setDefaultSku = (partNumber) => {
        let updatedSlectedSku = null;
        const skuData = this.sortedData;
        skuData.forEach((sku) => {
            if (sku.id.includes(partNumber)) {
                updatedSlectedSku = sku;
                return updatedSlectedSku;
            }
            return null;
        });
        if (updatedSlectedSku) {
            this.setState({
                selectedSku: updatedSlectedSku,
            });
        }
    }

    renderChatRegion = () => (
        (this.props.featureFlags['is-pdp-mobile-chat-enabled'] && this.isMobileChatEnabled && typeof document !== 'undefined' && !document?.querySelector('#footerChatRegion')) ? (
            <div className={this.props.classes.chatBlock}>
                <span className={this.props.classes.chatHeader}>Have Questions?</span>
                <p className={this.props.classes.chatText}>We will answer any questions, from finding a gift to placing an order</p>
                <span
                    onClick={() => {
                        trackEvent({
                            eventCategory: 'Mobile',
                            eventAction: 'Chat opened',
                            eventLabel: 'Chat',
                        });

                        triggerPopChatEvent({}, this.operatingSystem, this.props?.brand?.code, this.props.featureFlags, this.props.customerData);
                    }}
                    aria-hidden="true"
                    tabIndex="-1"
                    className={this.props.classes.chatIcon}
                    style={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                    <span className={this.props.classes.chatBox}>
                        <div className={this.props.classes.chatLayout}>
                            <span className={this.props.classes.chatIcon}>
                                <svg width="20" height="18" viewBox="0 0 20 18" fill="white" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M0.663788 4.78358C0.663788 2.65237 2.39148 0.924683 4.52269 0.924683H15.7486C17.8798 0.924683 19.6075 2.65237 19.6075 4.78358V10.0457C19.6075 12.1769 17.8798 13.9046 15.7486 13.9046H8.9078L5.599 17.2134C5.26714 17.5453 4.69975 17.3096 4.7007 16.8403L4.70668 13.9046H4.52269C2.39148 13.9046 0.663788 12.1769 0.663788 10.0457V4.78358ZM6.01362 8.81788C6.74017 8.81788 7.32916 8.2289 7.32916 7.50235C7.32916 6.7758 6.74017 6.18682 6.01362 6.18682C5.28707 6.18682 4.69809 6.7758 4.69809 7.50235C4.69809 8.2289 5.28707 8.81788 6.01362 8.81788ZM11.5389 7.50235C11.5389 8.2289 10.9499 8.81788 10.2233 8.81788C9.49678 8.81788 8.9078 8.2289 8.9078 7.50235C8.9078 6.7758 9.49678 6.18682 10.2233 6.18682C10.9499 6.18682 11.5389 6.7758 11.5389 7.50235ZM14.433 8.81788C15.1596 8.81788 15.7486 8.2289 15.7486 7.50235C15.7486 6.7758 15.1596 6.18682 14.433 6.18682C13.7065 6.18682 13.1175 6.7758 13.1175 7.50235C13.1175 8.2289 13.7065 8.81788 14.433 8.81788Z" />
                                </svg>
                            </span>
                            <span className={this.props.classes.liveChatText}>Live Chat</span>
                        </div>

                    </span>

                </span>
            </div>
        ) : null);

    // Track Product
    @track(trackProductData)
    render() {
        const {
            covidWarningOpen, hide18FCheckbox, isStandAloneMovieSku,
        } = this.state;

        const {
            classes,
            content,
            seoData,
            product,
            location,
            urlParams,
            featureFlags,
            presentation_family,
            brand,
            isPassportBundleItem,
            addToCartError,
            isPassportBundleProduct,
            passportData,
            productBaseContent,
            deviceType,
            showLoadingMessage,
            trackEventAction,
            yotpoCustomerReviewsGallery,
            isPassportAfterCta,
        } = this.props;
        if (!this.sortedData.length) {
            mbpLogger.logError({
                message: `PDP Rendered with No Available Skus: ${product?.name}, basecode: ${product?.partNumber}`,
                pageType: 'Product Page',
            });
            // redirect to out of stock
            return (
                <>
                    <GraphqlProductSeoSchema
                        name={product?.name}
                        shortDescription={product?.shortDescription}
                        partNumber={product?.partNumber}
                        productSkus={product?.productSkus}
                        location={location}
                        brand={brand}
                        reviews={product?.reviews}
                        productReviews={product?.productReviews}
                    />
                    <PDPSeoSchemaOutOfStock
                        brand={brand}
                        partNumber={productBaseContent?.entries?.[0]?.product_base_code}
                        name={productBaseContent?.entries?.[0]?.name}
                        location={location}
                    />
                    <ProductNotAvailableContainer
                        brand={brand}
                        productUnavailableName={product?.name}
                        code={product?.partNumber}
                        seoData={seoData}
                    />
                </>
            );
        }

        // Adding product details for personalization widget
        persistPage(this.props);
        // const skuData = product.productSkus;
        let skuConfig = {};
        let deliveryData = {};
        let smartGiftLetThemKnowSection = {};
        let passportMemberImg = '';
        let productUrl = '';
        let productDisplay = '';
        let smartGiftAddressSection = {};
        const giftProduct = {
            giftUrl: '',
            sku: '',
            collection: '',
        };
        // let giftUrl = '';
        // let sku = '';
        // let collection = '';

        if (content?.entries?.[0]) {
            skuConfig = content.entries[0].sku_section;
            giftProduct.giftUrl = content.entries[0].smart_gift_address_section?.link?.href;
            deliveryData = content?.entries?.[0].delivery_section;
            smartGiftLetThemKnowSection = content?.entries?.[0]?.smart_gift_let_them_know_section;
            smartGiftAddressSection = content?.entries?.[0].smart_gift_address_section;
            passportMemberImg = content.entries[0].passport_banner_section?.member_image;
            productUrl = content.entries[0].url;
            productDisplay = content?.entries?.[0]?.product_name_display;
        }

        const deliverySLA = product && product?.productSkus?.[0]?.availability?.defaultDeliveryMethod ? product?.productSkus?.[0]?.availability?.defaultDeliveryMethod : '';
        const deliveryIndicator = product && product?.productSkus?.[0] ? product?.productSkus?.[0]?.deliveryIndicator : '';

        if (product) {
            const {
                partNumber, shortDescription, longDescription, name, productSkus, reviews, productReviews, promoRestrictionContent,
            } = product;

            const {
                selectedSku, subscriptionDetails, subscription, clubSkuMonthGroup, clubUpsells,
                clubComponents, disableAddToCart, personalizedData, hasWine, isAddToCartEnabled,
                selectedClubUpsells, hasAddons, quantity, baseProduct, mobileAgeVerificationOpen,
                addToCartData, selectedAddons,
                validVaseConfiguratorSku,
            } = this.state;
            const breadCrumbArray = getBreadcrumbArray(product, location);
            const isCovidWarningPopupEnabled = featureFlags['is-pdp-covid-popup-warning-enabled'];
            let personalization;
            if (productSkus.length) {
                productSkus.forEach((sku) => {
                    personalization = sku.personalization;
                });
            }

            const enableMinicart = featureFlags['is-mini-cart-enabled'] || featureFlags['is-mini-cart-v2-enabled'];

            /**
             * @description PLA control
             * - based on featureFlag and urlParam in location.search
             * @return {bool} bool
             */
            const isPlaEnabled = () => {
                if (featureFlags['is-pla-flow-enabled'] && isProductListingAd(location.search)) {
                    return true;
                }
                return false;
            };

            /*
                ** LAYOUT RENDERING STARTS
            */

            // CYO Layout
            if (product?.productType === 'CUSTOM_ASSORTMENT') {
                return (
                    <div id="product_page_container" data-pagetype="product">
                        {/* TODO missing OG data */}
                        {enableMinicart && this.getMiniCart()}
                        <GraphqlCYOProductPageBuilder
                            presentation_family={presentation_family}
                            brand={brand}
                            featureFlags={featureFlags}
                            content={content}
                            seoData={seoData}
                            passportMemberImg={passportMemberImg}
                            isPassportBundleProduct={isPassportBundleProduct}
                            product={product}
                            urlParams={urlParams}
                            handleMiniCartModalClick={this.handleMiniCartModalClick}
                            cartError={addToCartError}
                            hasWine={hasWine}
                            mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                            handleMobileAgeVerification={this.handleMobileAgeVerification}
                            addToCartData={addToCartData}
                            deliveryData={deliveryData}
                            updateWineStatus={this.updateWineStatus}
                            deliverySLA={deliverySLA}
                            sortedData={this.sortedData}
                            hasAddons={hasAddons}
                            addonGroups={product.addonGroups || []}
                            subscriptionDetails={subscriptionDetails}
                            subscription={subscription}
                            disableAddToCart={disableAddToCart}
                            isAddToCartEnabled={isAddToCartEnabled}
                            selectedAddons={selectedAddons}
                            handleAddonChange={this.handleAddonChange}
                            handleRemoveAddon={this.handleRemoveAddon}
                            productBaseContent={productBaseContent}
                            isPassportAfterCta={isPassportAfterCta}
                        />
                        {this.renderChatRegion()}
                    </div>
                );
            }

            // Club Layout
            if (product?.productType?.toLowerCase() === 'club') {
                const { loader } = this.state;
                return (
                    <>
                        {loader && <GraphqlPortalLoader />}
                        {enableMinicart && this.getMiniCart()}
                        {addOgDynamicData(product, seoData, brand, location)}
                        <GraphqlProductSeoSchema
                            name={name}
                            shortDescription={shortDescription}
                            partNumber={partNumber}
                            productSkus={productSkus}
                            location={location}
                            brand={brand}
                            reviews={reviews}
                            productReviews={productReviews}
                        />
                        {/* TODO unused classname */}
                        <div className={classes.root} data-pagetype="product">
                            <GraphqlClubProductPage
                                addonGroups={product.addonGroups || []}
                                addToCartData={addToCartData}
                                breadCrumbArray={breadCrumbArray}
                                breadcrumbs={(<div className={`${classes.breadCrumbBody} ${classes.breadCrumbBody}--desktop`}> {getBreadcrumbArray(product, location)}</div>)}
                                changeClubLength={this.changeClubLength}
                                changeClubMonth={this.changeClubMonth}
                                changeClubUpsell={this.changeClubUpsell}
                                clubComponents={clubComponents}
                                clubUpsells={clubUpsells}
                                deliveryData={deliveryData}
                                deliveryIndicator={deliveryIndicator}
                                deliverySLA={deliverySLA}
                                deviceType={deviceType}
                                disableAddToCart={disableAddToCart}
                                getUpdatedPersonalizedProdData={this.getUpdatedPersonalizedProdData}
                                handleMiniCartModalClick={this.handleMiniCartModalClick}
                                handleMobileAgeVerification={this.handleMobileAgeVerification}
                                handlePersonalizeInputChange={this.handlePersonalizeInputChange}
                                handleSkuChange={this.handleSkuChange}
                                hasAddons={hasAddons}
                                hasWine={hasWine}
                                isPassportEnabled={product.isPassportEligible}
                                isAddToCartEnabled={isAddToCartEnabled}
                                location={location}
                                longDescription={longDescription}
                                mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                                passportMemberImg={passportMemberImg}
                                personalization={personalization}
                                personalizedData={personalizedData}
                                presentation_family={presentation_family}
                                product={product}
                                productUrl={productUrl}
                                selectedClubUpsells={selectedClubUpsells}
                                selectedSku={selectedSku}
                                shortDescription={shortDescription}
                                skuConfig={skuConfig}
                                skuMonths={clubSkuMonthGroup}
                                sortedData={this.sortedData}
                                isPassportAfterCta={isPassportAfterCta}
                            />
                            <div className={`${classes.breadCrumbBody} ${classes.breadCrumbBody}--mobile club-mobile-pdp`}>{getBreadcrumbArray(product, location)}</div>
                        </div>
                        {this.renderChatRegion()}
                    </>
                );
            }
            // Normal + PLA Layout
            return (
                <>
                    {isCovidWarningPopupEnabled && <CovidWarningPopup isOpen={covidWarningOpen} closeModal={this.closeCovidWarning} />}
                    {addOgDynamicData(product, seoData, brand, location)}
                    {showLoadingMessage && <SiteLoader />}
                    {enableMinicart && this.getMiniCart()}
                    <GraphqlProductSeoSchema
                        name={name}
                        shortDescription={shortDescription}
                        partNumber={partNumber}
                        productSkus={productSkus}
                        location={location}
                        brand={brand}
                        reviews={reviews}
                        productReviews={productReviews}
                    />
                    <StandardProductsPage
                        trackEvent={trackEventAction}
                        addToCartData={addToCartData}
                        baseProduct={baseProduct}
                        validVaseConfiguratorSku={validVaseConfiguratorSku}
                        setVaseConfiguratorSku={this.setVaseConfiguratorSku}
                        brand={brand}
                        content={content}
                        deliveryData={deliveryData}
                        deliveryIndicator={deliveryIndicator}
                        deliverySLA={deliverySLA}
                        deviceType={deviceType}
                        disableAddToCart={disableAddToCart}
                        featureFlags={featureFlags}
                        getUpdatedPersonalizedProdData={this.getUpdatedPersonalizedProdData}
                        handleMiniCartModalClick={this.handleMiniCartModalClick}
                        handleMobileAgeVerification={this.handleMobileAgeVerification}
                        handlePersonalizeInputChange={this.handlePersonalizeInputChange}
                        handleSkuChange={this.handleSkuChange}
                        handleSkuClick={this.handleSkuClick}
                        hasAddons={hasAddons}
                        hasWine={hasWine}
                        hide18FCheckbox={hide18FCheckbox}
                        isAddToCartEnabled={isAddToCartEnabled}
                        isPassportBundleItem={isPassportBundleItem}
                        isPassportBundleProduct={isPassportBundleProduct}
                        isPlaEnabled={isPlaEnabled()}
                        isStandAloneMovieSku={isStandAloneMovieSku}
                        location={location}
                        mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                        passportData={passportData}
                        passportMemberImg={passportMemberImg}
                        personalization={personalization}
                        personalizedData={personalizedData}
                        presentation_family={presentation_family}
                        product={product}
                        productBaseContent={productBaseContent}
                        productDisplay={productDisplay}
                        productUrl={productUrl}
                        quantity={quantity}
                        selectedSku={selectedSku}
                        setProductQuantity={this.setProductQuantity}
                        skuConfig={skuConfig}
                        smartGiftAddressSection={smartGiftAddressSection}
                        smartGiftLetThemKnowSection={smartGiftLetThemKnowSection}
                        sortedData={this.sortedData}
                        subscription={subscription}
                        subscriptionDetails={subscriptionDetails}
                        triggerSubscriptionCheck={this.triggerSubscriptionCheck}
                        updateWineStatus={this.updateWineStatus}
                        urlParams={urlParams}
                        promoRestrictionContent={promoRestrictionContent}
                        yotpoCustomerReviewsGallery={yotpoCustomerReviewsGallery}
                        isPassportAfterCta={isPassportAfterCta}
                    />
                    {this.renderChatRegion()}
                </>
            );
        }
        return <PDPSkeleton />;
    }
}

GraphqlProductPageBuilder.defaultProps = {
    showLoadingMessage: false,
    baseProductPartNumber: null,
    passportData: {},
    isPassportBundleProduct: {},
    brand: {},
    addToCartError: '',
    gciData: {},
    yotpoCustomerReviewsGallery: {},
    selectedBYOItems: () => {},
    smallestSkuSelectedEnabled: false,
    removeCardIsleData: () => {},
    isPassportAfterCta: false,
    experimentParams: {},
};

GraphqlProductPageBuilder.propTypes = {
    classes: object.isRequired,
    content: object.isRequired,
    productBaseContent: object.isRequired,
    product: object.isRequired,
    location: object.isRequired,
    loadSr: func.isRequired,
    resetAgeVerificationFlag: func.isRequired,
    actionSelectedInlineAddons: func.isRequired,
    setLoader: func.isRequired,
    seoData: shape({
        seo_searchable: bool,
        page_title: string,
        keywords: string,
        meta_description: string,
        og: array,
    }).isRequired,
    urlParams: object.isRequired,
    isPla: bool.isRequired,
    featureFlags: object.isRequired,
    customerData: object.isRequired,
    presentation_family: string.isRequired,
    brand: shape({
        domain: string.isRequired,
    }),
    isPassportBundleItem: bool.isRequired,
    client: object.isRequired, // Apollo Client
    showLoadingMessage: bool,
    removeGCIData: func.isRequired,
    baseProductPartNumber: string,
    callClearAddToCartError: func.isRequired,
    passportData: shape({
        passport_bundle: {
            pdpage_passport_bundle_title: string,
        },
        passport_up_sell_conditional_logic_at_entrance: shape({
            enable: bool,
            utm_param: string,
        }),
        passport_messaging: shape({
            enable_member_message: bool,
            member_message: string,
        }),
    }),
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    locationType: string.isRequired,
    deviceType: string.isRequired,
    actionSetBrandMovieSkus: func.isRequired,
    trackEventAction: func.isRequired,
    dispatchEmitCustomTrackingEvent: func.isRequired,
    callRemovePmallItem: func.isRequired,
    addToCartError: string,
    removeGCIKeyData: func.isRequired,
    gciData: shape({
        product_sku: string,
    }),
    history: shape({
        push: func,
    }).isRequired,
    yotpoCustomerReviewsGallery: shape({
        gallery_id: string,
        heading: string,
        sub_heading: string,
    }),
    actionSetMiniCartModalState: func.isRequired,
    actionSetDonationCategory: func.isRequired,
    subscriptionBannerType: string.isRequired,
    selectedBYOItems: func,
    smallestSkuSelectedEnabled: bool,
    removeCardIsleData: () => {},
    isPassportAfterCta: bool,
    experimentParams: object,
};

const mapStateToProps = (state) => ({
    showLoadingMessage: getAddToCartLoaderStatus(state),
    addToCartError: getAddToCartError(state),
    passportData: getPassportData(state),
    locationType: getLocationType(state),
    deviceType: getSSRDeviceType(state),
    gciData: getGCIData(state),
    countryName: country(state), // Used for tracking recently viewed products
    subscriptionBannerType: getActiveABTest('pdp_subsriptoin_banner')(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators(
    {
        loadSr: shopRunnerActions.loadShopRunner,
        resetAgeVerificationFlag: setAgeVerifyFlagInvalid,
        actionSelectedInlineAddons: setSelectedInlineAddons,
        removeGCIData: clearGCIData,
        callClearAddToCartError: clearAddToCartError,
        actionSetBrandMovieSkus: setBrandMovieSkus,
        trackEventAction: trackEvent,
        dispatchEmitCustomTrackingEvent: emitCustomTrackingEvent,
        removeGCIKeyData: removeGCIKey,
        callRemovePmallItem: removePmallItem,
        setLoader: loadAddToCartloader,
        actionSetMiniCartModalState: bindActionCreators(setMiniCartModalState, dispatch),
        actionSetDonationCategory: bindActionCreators(setDonationsCategory, dispatch),
        selectedBYOItems: bindActionCreators(setSelectedBYOItems, dispatch),
        removeCardIsleData: bindActionCreators(setCardIsleData, dispatch),
    },
    dispatch,
);

const enhance = compose(
    trackPage(),
    withRouter,
    withApollo,
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlProductPageBuilder);
